import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./GamePlay.css";
import logo from "./assets/logo.png";
import click from "./assets/sounds/click.mp3";
import tictac from "./assets/sounds/tic-tac.mp3";
import { usePreferences } from "./hooks/usePreferences";
import { useTranslation } from "react-i18next";
import { apiFetch } from "../api/api"; // ✅ import centralisé pour les requêtes

export default function GamePlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, game } = location.state || {};
  const token = localStorage.getItem("token") || "";
  const { soundEnabled, timerSoundEnabled } = usePreferences(token);

  const [step, setStep] = useState(1);
  const [category, setCategory] = useState(null);
  const [difficulty, setDifficulty] = useState(null);
  const [domain, setDomain] = useState(null);
  const [question, setQuestion] = useState(null);
  const [error, setError] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [hasDrawn, setHasDrawn] = useState(false);

  const timerRef = useRef(null);
  const timerAudioRef = useRef(null); // 🔊 ref pour le son tic-tac

  const API_URL = process.env.REACT_APP_API_URL;

  const language = profile?.preferredLanguage || "fr";

  const { t, i18n } = useTranslation();

    useEffect(() => {
      if (profile?.preferredLanguage) {
        i18n.changeLanguage(profile.preferredLanguage.toLowerCase());
      }
    }, [profile?.preferredLanguage]);
  
  // Fonction centralisée pour jouer les sons
  const playSound = (sound, isTimer = false) => {
    if ((isTimer && !timerSoundEnabled) || (!isTimer && !soundEnabled)) return;

    if (isTimer) {
      // stoppe un éventuel son déjà lancé
      if (timerAudioRef.current) {
        timerAudioRef.current.pause();
        timerAudioRef.current.currentTime = 0;
      }
      timerAudioRef.current = new Audio(sound);
      timerAudioRef.current.loop = true; // tic-tac en continu
      timerAudioRef.current.play();
    } else {
      const audio = new Audio(sound);
      audio.play();
    }
  };

  const stopTimerSound = () => {
    if (timerAudioRef.current) {
      timerAudioRef.current.pause();
      timerAudioRef.current.currentTime = 0;
      timerAudioRef.current = null;
    }
  };

const categories = [
  { id: 1, labelKey: "categories.history", color: "#5AA5B0" },
  { id: 2, labelKey: "categories.geography", color: "#0B7C5A" },
  { id: 3, labelKey: "categories.tasting", color: "#2B303A" },
  { id: 4, labelKey: "categories.jobs", color: "#d74933" },
  { id: 5, labelKey: "categories.domains", color: "#b157b0ff" },
];

  const domains = [
  { id: 1, labelKey: "domains.loire", color: "#688082" },
  { id: 2, labelKey: "domains.champagne", color: "#bf9876" },
  { id: 3, labelKey: "domains.alsace", color: "#b4b68a" },
  { id: 4, labelKey: "domains.bourgogne", color: "#925f38" },
  { id: 5, labelKey: "domains.beaujolais", color: "#627e68" },
  { id: 6, labelKey: "domains.rhone", color: "#a21958" },
  { id: 7, labelKey: "domains.provence", color: "#a3575a" },
  { id: 8, labelKey: "domains.languedoc", color: "#7D5667" },
  { id: 9, labelKey: "domains.bordeaux", color: "#7C504B" },
  { id: 10, labelKey: "domains.corsica", color: "#e4c512" },
];

const difficulties = [
  { id: 1, labelKey: "difficulties.primeur", color: "#7465AA" },
  { id: 2, labelKey: "difficulties.grandCru", color: "#3078BD" },
  { id: 3, labelKey: "difficulties.vintage", color: "#F5BC28" },
];

  const startGame = async () => {
    playSound(tictac, true); // 🎵 tic-tac en boucle
    try {
      let url = `/api/game/play?categoryId=${category.id}&lang=${language}`;
      if (category.id === 5 && domain) url += `&domainId=${domain.id}`;
      else if (difficulty) url += `&difficulty=${difficulty.id}`;

      // ✅ Requête centralisée
      const data = await apiFetch(url, { token });

      setQuestion(data);
      setShowAnswer(false);
      setError(null);

      // reset timer à 15 secondes
      setTimeLeft(60);
      if (timerRef.current) clearInterval(timerRef.current);

      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setShowAnswer(true);
            stopTimerSound(); // 🛑 stop du son si le temps est écoulé
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error("Erreur lors du tirage :", err);
      setError("Impossible de charger une question.");
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    stopTimerSound(); // 🛑 coupe le tic-tac quand on affiche la réponse
  };

  const resetGame = () => {
    setStep(1);
    setCategory(null);
    setDifficulty(null);
    setDomain(null);
    setQuestion(null);
    setError(null);
    setShowAnswer(false);
    setTimeLeft(60);
    setHasDrawn(false); // 🔹 réaffiche le bouton
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    stopTimerSound(); // 🛑 coupe aussi au reset
  };

  const renderButtons = (items, onClick) =>
    items.map((item) => (
      <button
        key={item.id}
        onClick={() => {
          playSound(click);
          onClick(item);
        }}
        style={{
          backgroundColor: item.color,
          color: "#fff",
          padding: "12px",
          borderRadius: "12px",
          marginBottom: "12px",
          fontWeight: "bold",
          cursor: "pointer",
          width: "100%",
          maxWidth: "80%"
        }}
      >
        {t(item.labelKey)}
      </button>
    ));

  const handleBack = () => {
    if (step === 1) navigate(-1);
    else if (step === 2) {
      setStep(1);
      setCategory(null);
    } else if (step === 3) {
      setStep(2);
      setDifficulty(null);
      setDomain(null);
      setQuestion(null);
      setError(null);
      setShowAnswer(false);
      stopTimerSound();
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopTimerSound();
    };
  }, []);

  return (
    <div
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "100%",
    maxWidth: "650px", // 🔹 largeur max souhaitée
    margin: "0 auto", // 🔹 centre horizontalement
    minHeight: "100vh",
    position: "relative",
    padding: "20px",
  }}
>
      {/* Flèche retour */}
      {!hasDrawn && (
        <button className="back-button" onClick={() => { playSound(click); handleBack(); }}>
  ←
</button>

      )}

      <div className="gameplay-container">
        {profile && (
          <div className="top-right-avatar">
            <img src={profile.avatar} alt={profile.pseudo} className="avatar" />
          </div>
        )}
      </div>

      <img
        src={logo}
        alt="VinOdys’sait"
        className="logo"
        style={{ marginBottom: "24px" }}
      />

      {/* Step 1 */}
      {step === 1 && (
        <>
          <h2 style={{ marginBottom: "1rem" }}>{t("gameplay.chooseCategory")}</h2>
          <div style={{ width: "100%", maxWidth: "350px" }}>
            {renderButtons(categories, (c) => {
              setCategory(c);
              setStep(2);
            })}
          </div>
        </>
      )}

      {/* Step 2 */}
      {step === 2 && category && category.id !== 5 && (
        <>
          <h2 style={{ marginBottom: "1rem" }}>{t("gameplay.chooseLevel")}</h2>
          <div style={{ width: "100%", maxWidth: "350px" }}>
            {renderButtons(difficulties, (d) => {
              setDifficulty(d);
              setStep(3);
            })}
          </div>
        </>
      )}
      {step === 2 && category && category.id === 5 && (
        <>
          <h2 style={{ marginBottom: "1rem" }}>{t("gameplay.chooseDomain")}</h2>
          <div style={{ width: "100%", maxWidth: "350px" }}>
            {renderButtons(domains, (d) => {
              setDomain(d);
              setStep(3);
            })}
          </div>
        </>
      )}

      {/* Step 3 */}
      {step === 3 && (
       <>
          {!hasDrawn && (
            <>
          <h2 style={{ marginBottom: "1rem" }}>{t("gameplay.ready")}</h2>
            <button
              onClick={() => {
                playSound(click);
                setHasDrawn(true); // 🔹 cache le bouton après le clic
                startGame();
              }}
              style={{
                backgroundColor: "#702632",
                color: "#fff",
                padding: "12px",
                borderRadius: "12px",
                fontWeight: "bold",
                cursor: "pointer",
                marginBottom: "16px",
                width: "100%",
                maxWidth: "350px",
              }}
            >
              {t("gameplay.drawQuestion")}
            </button>
          </>
          )}

          {error && (
            <p style={{ color: "red", marginBottom: "16px" }}>{error}</p>
          )}

          {question && (
            <div
              style={{
                backgroundColor: "#ffffff00",
                padding: "16px",
                borderRadius: "12px",
                maxWidth: "100%",
                maxWidth: "600px",
              }}
            >
              <div style={{ fontWeight: "bold", fontSize: "20px" }}>
                {question.questionText.replace(/\\n/g, '\n').split('\n').map((line, index) => (
                  <p key={index} style={{ margin: "4px 0" }}>
                    {line}
                  </p>
                ))}
              </div>

              {!showAnswer && (
                <p
                  style={{
                    color: timeLeft <= 15 ? "#E3342F" : "#CBD5E0",
                    marginTop: "8px",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  {t("gameplay.timeLeft", { time: timeLeft })}
                </p>
              )}

              {!showAnswer ? (
                <button
                  onClick={() => {
                    playSound(click);
                    handleShowAnswer();
                  }}
                  style={{
                    marginTop: "12px",
                    padding: "10px",
                    borderRadius: "10px",
                    width: "60%",
                    backgroundColor: "#60a561",
                    color: "#fff",
                    fontWeight: "bold",
                  }}
                >
                  {t("gameplay.seeAnswer")}
                </button>
              ) : (
                <>
                  <div style={{ marginTop: "12px", color: "#E2E8F0" }}>
                  <strong>{t("gameplay.answer")}</strong>
                  {question.answerText.replace(/\\n/g, '\n').split('\n').map((line, index) => (
                    <p key={index} style={{ margin: "4px 0" }}>
                      {line}
                    </p>
                  ))}
                </div>
                  <button
                    onClick={() => {
                      playSound(click);
                      resetGame();
                    }}
                    style={{
                      marginTop: "16px",
                      padding: "10px",
                      borderRadius: "10px",
                      width: "60%",
                      backgroundColor: "#1c5253",
                      color: "#fff",
                      fontWeight: "bold",
                    }}
                  >
                    {t("gameplay.newQuestion")}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
